import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "harley_admin_token";

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).join(" ") : "Something went wrong.");

const Login = ({ onToken }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem(KEY, data.token);
      onToken(data.token);
    } catch (err) {
      setError(errText(err.response?.data?.detail));
    }
  };
  const field = "mt-2 h-12 w-full rounded-xl border border-[var(--blush-deep)] bg-white px-4 outline-none focus:border-[var(--mauve)]";
  return (
    <form onSubmit={submit} className="mx-auto mt-24 w-full max-w-sm rounded-3xl bg-white p-8 shadow-lg" data-testid="admin-login-form">
      <h1 className="font-display text-3xl">Sign-ups</h1>
      <p className="mt-1 text-sm text-[var(--muted)]">Owner login</p>
      <label className="mt-6 block text-sm font-semibold" htmlFor="ad-email">Email</label>
      <input id="ad-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={field} data-testid="admin-email-input" />
      <label className="mt-4 block text-sm font-semibold" htmlFor="ad-pw">Password</label>
      <input id="ad-pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={field} data-testid="admin-password-input" />
      {error && <p className="mt-3 text-sm text-red-700" role="alert" data-testid="admin-login-error">{error}</p>}
      <button className="mt-6 h-12 w-full rounded-full bg-[var(--mauve)] font-semibold text-white" data-testid="admin-login-button">Log in</button>
    </form>
  );
};

const toCsv = (rows) =>
  ["Name,Email,Signed up", ...rows.map((r) => [r.name, r.email, r.created_at].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");

const List = ({ token, onLogout }) => {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/signups`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else setError("Could not load sign-ups.");
    }
  }, [token, onLogout]);
  useEffect(() => { load(); }, [load]);
  const download = () => {
    const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "signups.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="mx-auto max-w-4xl px-5 py-14" data-testid="admin-signups">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-4xl">Sign-ups <span className="text-[var(--mauve)]" data-testid="admin-signup-count">({rows.length})</span></h1>
        <div className="flex gap-3">
          <button onClick={download} disabled={!rows.length} className="flex items-center gap-2 rounded-full bg-[var(--mauve)] px-5 py-3 text-sm font-semibold text-white disabled:opacity-50" data-testid="admin-download-csv"><Download className="h-4 w-4" />Download CSV</button>
          <button onClick={onLogout} className="flex items-center gap-2 rounded-full border border-[var(--mauve)] px-5 py-3 text-sm font-semibold text-[var(--mauve)]" data-testid="admin-logout"><LogOut className="h-4 w-4" />Log out</button>
        </div>
      </div>
      {error && <p className="mt-6 text-red-700">{error}</p>}
      <div className="mt-8 overflow-x-auto rounded-2xl border border-[var(--blush-deep)] bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-[var(--cream)] text-[var(--muted)]"><tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Signed up</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[var(--blush-deep)]" data-testid="admin-signup-row">
                <td className="p-4">{r.name}</td><td className="p-4">{r.email}</td><td className="p-4">{new Date(r.created_at).toLocaleString()}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={3} className="p-8 text-center text-[var(--muted)]">No sign-ups yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(KEY));
  const logout = useCallback(() => { sessionStorage.removeItem(KEY); setToken(null); }, []);
  return <div className="min-h-screen bg-[var(--cream)]">{token ? <List token={token} onLogout={logout} /> : <Login onToken={setToken} />}</div>;
}
